import { OnInit, Component, ViewChild, forwardRef, Input, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { SessionStorageService, selectorAuth } from '@app/core';
import { environment } from '@env/environment';
import { InfoService } from '@app/core/info/info.service';
import { BusinessDownloadService } from '@app/business/business-download.service';
import { HistoryService } from '@app/certificates/history/history.service';
import { BusinessRequestedHistoryInitService } from '@app/business/business-history/business-requested-history/services/business-requested-history-init.service';
import { RequestedHistory, RequestedHistoryRequest } from '@app/certificates/history/history';
import { HistoryDetailsComponent } from '@app/business/history-details/history-details.component';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { ICertificateData } from '@app/certificates/manage/manage';
import { IUserData } from '@app/profile/main/details';
import { SortableClolumsNames, DocumentStatus, DocumentStatusMap, ParcelType, DocumentTypeMap } from '@app/business/business-models';
import { IParcelTypeOptions, IStatusesAndLabelsForParcel, ParcelStatus,} from './business-in-copy-history.models';

@Component({
  selector: 'cloud-business-in-copy-history',
  templateUrl: './business-in-copy-history.component.html',
  styleUrls: ['./business-in-copy-history.component.scss']
})
export class BusinessInCopyHistoryComponent {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(forwardRef(() => MatSort)) sort: MatSort;
  @Input() isSubcomponent = false;
  unsubscribe$: Subject<void> = new Subject<void>();
  signingHistoryDataSource: MatTableDataSource<RequestedHistory> = new MatTableDataSource<RequestedHistory>();

  displayedColumns: string[]; // Columns displayed in the table

  parcelTypeOptions: IParcelTypeOptions[]; // Options for the parcel type filter dropdown
  statusesAndLabelsForParcel: IStatusesAndLabelsForParcel[]; // Options for the batch signing and registered mail status filter dropdown

  displayOptions: RequestedHistoryRequest;
  readonly dateFormat = environment.dateFormat;
  readonly timeFormat = environment.timeFormat;
  certificateNumbers: string[];
  displayAllCertsOption = false;
  certificates: ICertificateData[];
  fromDate: string;
  toDate: string;
  minEndDateValue: string;
  maxStartDateValue: string;
  documentNumber: any;
  isLoading = true
  signedfileName: any;
  resetCertificate: string;
  userData: IUserData;
  downloadIds = [];

  constructor(
    public info: InfoService,
    private historyService: HistoryService,
    public tableHelper: MatTableHelper,
    private store: Store<any>,
    public dialog: MatDialog,
    private sessionStorageService: SessionStorageService,
    private router: Router,
    private initService: BusinessRequestedHistoryInitService,
    private renderer: Renderer2,
    private businessDownloadService: BusinessDownloadService
  ) {
  }

  ngOnInit() {
    // Initialize table columns, parcel type options, batch signing status labels, and registered mail status labels
    this.displayedColumns = this.initService.setDisplayedColumns();
    this.parcelTypeOptions = this.initService.setParcelTypeOptions();
    this.statusesAndLabelsForParcel = this.initService.setParcelStatusLabels();

    this.initDisplayOptions();
    this.getUserInfo();
    this.loadRequestedHistory();
  }

  getUserInfo() {
    this.store
      .select(selectorAuth)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (res.userData) { this.userData = res.userData; }
      });
  }

  private initDisplayOptions() {
    this.displayOptions = {};
    this.displayOptions.offset = 0;
    this.displayOptions.limit = 10;
    this.displayOptions.isEmployeeCc = true;
  }

  loadRequestedHistory() {
    this.displayOptions.employeeCmrId = this.userData.cmrId;
    this.displayOptions.companyCmrId = this.sessionStorageService.getItem("businessAccountId");
    this.historyService.getRequestsHistory(this.displayOptions, true).subscribe(res => {
      if (res?.data?.content) {
        this.setDataSource(res.data.content);
        this.documentNumber = res.data.size;
        this.setPaginator();
      } else if (res?.data?.message) {
        this.info.notice(res.data.message);
        this.isLoading = false;
      } else {
        this.isLoading = false;
      }
    });
  }

  // tslint:disable-next-line:no-shadowed-variable
  setDataSource(requestedHistory: RequestedHistory[]) {
    this.isLoading = false;
    let dataSource = [];

    if (requestedHistory) {
      dataSource = requestedHistory.map(a => Object.assign({
        date: a.ts,
        downloadeTimestamp: a.downloadTs,
        packageName: a.name,
        relyingPartyId: a.rpId,
        relyingPartyName: a.rpName,
        status: a.status,
        statusIcon: DocumentStatusMap.get(DocumentStatus[a.status]),
        fileId: a.fileId,
        signCountTxt: a.signCount + '/' + a.reqSignCount,
        signCount: a.signCount,
        reqSignCount: a.reqSignCount,
        requestedId: a.id,
        messageIdentifier: a.serviceEventId,
        docCount: a.docCount,
        parcelType: a.parcelType,
        isRegisteredMail: a.parcelType === ParcelType.REGISTERED_DELIVERY,
        typeIcon: DocumentTypeMap.get(a.parcelType as ParcelType),
        isDownloadable: this.isDownloadable(a),
        isResendable: this.isResendable(a),
        isRegisteredMailReceiptDownloadable: this.isRegisteredMailReceiptDownloadable(a)
      }));
    }
    this.signingHistoryDataSource = new MatTableDataSource(dataSource);
  }

  hasData() {
    return (this.signingHistoryDataSource.data.length !== 0);
  }

  setParcelType() {
    if (this.displayOptions.parcelType === 'ALL') {
      delete this.displayOptions.parcelType;
    }

    this.displayOptions.offset = 0;
  }

  setOrderStatus() {
    if (this.displayOptions.status === 'ALL') {
      delete this.displayOptions.status;
    }

    this.displayOptions.offset = 0;
  }

  private setPaginator() {
    this.paginator.pageIndex = this.displayOptions.offset;
    this.paginator.pageSize = this.displayOptions.limit;
    this.paginator.length = this.documentNumber;
  }

  setRequestDates() {
    const pip: DatePipe = new DatePipe('bg-BG');

    if (this.fromDate) {
      this.minEndDateValue = this.fromDate;
      const startDate = new Date(this.fromDate).setHours(0, 0, 0, 0);
      this.displayOptions.fromCreatedDate = encodeURIComponent(pip.transform(startDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ'));
    }

    if (this.toDate) {
      this.maxStartDateValue = this.toDate;
      const endDate = new Date(this.toDate).setHours(23, 59, 59, 999);
      this.displayOptions.toCreatedDate = encodeURIComponent(pip.transform(endDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ'));
    }
    this.displayOptions.offset = 0;
  }

  searchRequestedPackage() {
    if (this.displayOptions.textToShow) {
      this.displayOptions.offset = 0;
    }
    this.loadRequestedHistory();
  }

  clearText() {
    this.displayOptions.textToShow = '';
    delete this.displayOptions.textToShow;
    this.displayOptions.offset = 0;
  }

  resetFilters() {
    this.fromDate = '';
    this.maxStartDateValue = '';
    this.toDate = '';
    this.minEndDateValue = '';
    this.resetCertificate = '';
    this.initDisplayOptions();
    this.loadRequestedHistory();
  }

  sortByHeader(sorter) {
    const columnName = this.getColumnName(sorter.id);
    if (!sorter._arrowDirection) {
      sorter._arrowDirection = 'desc';
    }
    this.displayOptions.orderByFilter = columnName;
    this.displayOptions.order = sorter._arrowDirection.toUpperCase();
    this.loadRequestedHistory();
  }


  getColumnName(name): string {
    switch (name) {
      case 'date':
        return SortableClolumsNames.DATE;
      case 'packageName':
        return SortableClolumsNames.TEXT_TO_SHOW;
      case 'certSerialNumber':
        return SortableClolumsNames.SERIAL_NUMBER;
      case 'relyingPartyName':
        return SortableClolumsNames.RELYING_PARTY;
      case 'signedDate':
        return SortableClolumsNames.SIGNED_DATE;
      case 'downloadeTimestamp':
        return SortableClolumsNames.DOWNLOAD_TS;
      default:
        return SortableClolumsNames.DATE;
    }
  }

  isDownloadable(packageData: RequestedHistory) {
    // Allow download for Registered mail no matter the status
    if (packageData.parcelType === ParcelType.REGISTERED_DELIVERY) {
      return true;
    }

    // Logic for bach signing
    const status = packageData.status
    if (DocumentStatusMap.get(DocumentStatus[status]).key == ParcelStatus.SIGNED
      || DocumentStatusMap.get(DocumentStatus[status]).key == ParcelStatus.ARCHIVED
      || DocumentStatusMap.get(DocumentStatus[status]).key == ParcelStatus.RECEIVED) {
      return true;
    } else {
      return false;
    }
  }

  isResendable(packageData: RequestedHistory) {
    if (DocumentStatusMap.get(DocumentStatus[packageData.status]).key == ParcelStatus.EXPIRED) {
      return true;
    } else {
      return false;
    }
  }

  isRegisteredMailReceiptDownloadable(packageData: RequestedHistory) {
    // Only for Registered mail
    if (packageData.parcelType !== ParcelType.REGISTERED_DELIVERY) {
      return false;
    }

    // Allowed status for receipt
    if (packageData.status === DocumentStatus.SENT
      || packageData.status === DocumentStatus.DELIVERED
      || packageData.status === DocumentStatus.VIEWED
      || packageData.status === DocumentStatus.FAILED
    ) {
      return true;
    }

    return false;
  }

  onDownloadPackage(pack: any): void {
    this.businessDownloadService.fetchPackageFiles(pack.requestedId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => this.businessDownloadService.downloadBlobFileToClientPC(res),
        error: (err) => this.info.error(err.message)
      });
  }


  onDownloadRegisteredMailReceipt(rowData) {
    const messageIdentifier = rowData.messageIdentifier;

    this.businessDownloadService.requestForReceiptData(messageIdentifier)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.businessDownloadService.downloadReceipt(res.data.pdfData, this.renderer);
        },
        error: (err) => {
          this.info.error(err.message);
        }
      })
  }

  onPaginateChange(paginator) {
    this.displayOptions.limit = paginator.pageSize;
    this.displayOptions.offset += (paginator.pageIndex - paginator.previousPageIndex);
    this.loadRequestedHistory();
  }

  openRequestedDetailsDialog(rowData) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = rowData

    dialogConfig.panelClass = 'panel-class-history';

    const dialogRef = this.dialog.open(HistoryDetailsComponent, dialogConfig);
    const sub = dialogRef.componentInstance.close.subscribe(() => {
      this.dialog.closeAll();
    });
  }

  onResendPackage(pack: any) {
    this.router.navigate(['business/signing'], { state: { requestedId: pack.requestedId, signCount: pack.signCount } });
  }
}
